import { v1, toResult } from '@/infrastructure/api/v1/client';
import { discardTemplateCopy } from '@/infrastructure/clients/scenesApi';
import type { useServices } from '@/infrastructure/providers/ServiceProvider';
import { scenePatchBody, sceneToLegacy } from '@/infrastructure/api/v1/mappers/scenes';
import { editorWrites, untracked } from '@/modules/undoRedo/sceneWrites';
import { safeParseJson } from '@/utils/safeParseJson';

export const TEMPLATE_COPY_CLAIMED_KEY = 'template_copy_claimed';
const TEMPLATE_COPY_OPENED_AT_KEY = 'template_copy_opened_at';

type ScenePayload = Record<string, unknown>;

export function isTemplateCopyClaimed(scenePayload: ScenePayload | string | null | undefined): boolean {
  return safeParseJson<ScenePayload>(scenePayload)?.[TEMPLATE_COPY_CLAIMED_KEY] === true;
}

export function isMarkedUnkept(scenePayload: ScenePayload | string | null | undefined): boolean {
  return safeParseJson<ScenePayload>(scenePayload)?.[TEMPLATE_COPY_CLAIMED_KEY] === false;
}

export const claimTemplateCopy = (sceneInstanceId: string) =>
  updatePayload(sceneInstanceId, current => (current[TEMPLATE_COPY_CLAIMED_KEY] === true ? null : { [TEMPLATE_COPY_CLAIMED_KEY]: true }));

export const markTemplateCopyUnclaimed = (sceneInstanceId: string) =>
  updatePayload(sceneInstanceId, current => (current[TEMPLATE_COPY_CLAIMED_KEY] === false ? null : { [TEMPLATE_COPY_CLAIMED_KEY]: false }));

export const touchTemplateCopy = (sceneInstanceId: string) =>
  updatePayload(sceneInstanceId, () => ({ [TEMPLATE_COPY_OPENED_AT_KEY]: new Date().toISOString() }));

async function updatePayload(sceneInstanceId: string, changes: (current: ScenePayload) => ScenePayload | null): Promise<void> {
  await editorWrites.idle([`scene:${sceneInstanceId}`]);
  const { data, error: readError } = await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}', {
    params: { path: { sceneUniqueId: sceneInstanceId } },
  }));
  if (readError) throw readError;

  const current = safeParseJson<ScenePayload>(data ? sceneToLegacy(data).scene_instance_payload : null) ?? {};
  const next = changes(current);
  if (!next) return;

  const { error } = await toResult(v1.PATCH('/api/v1/scenes/{sceneUniqueId}', {
    params: { path: { sceneUniqueId: sceneInstanceId } },
    body: scenePatchBody({ scene_instance_payload: JSON.stringify({ ...current, ...next }) }),
    headers: untracked.headers,
  }));
  if (error) throw error;
}

export async function removeTemplateCopy(services: ReturnType<typeof useServices>, storyId: string): Promise<void> {
  await discardTemplateCopy(storyId);
  services.library?.invalidateMyAssetTypes?.();
  window.dispatchEvent(new CustomEvent('libraryRefetchNeeded'));
}
